"use client";
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import SplitType from 'split-type';

type SplitTypeInstance = {
  chars: HTMLElement[];
  revert: () => void;
};

interface PreloaderProps {
  onComplete?: () => void;
}

const Preloader = ({ onComplete }: PreloaderProps): JSX.Element | null => {
  const preloaderContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const currentRoute = usePathname();
  const animationRef = useRef<gsap.core.Timeline | null>(null);
  const splitInstance = useRef<SplitTypeInstance | null>(null);

  const cleanupAnimation = useCallback(() => {
    if (animationRef.current) {
      animationRef.current.kill();
      animationRef.current = null;
    }
    if (splitInstance.current) {
      splitInstance.current.revert();
      splitInstance.current = null;
    }
  }, []);

  useEffect(() => {
    if (currentRoute !== '/') {
      setIsLoading(false);
      onComplete?.();
      return;
    }

    const timer = setTimeout(() => {
      if (!preloaderContainerRef.current) return;

      const textElement = preloaderContainerRef.current.querySelector('.text-preloader');
      if (!textElement) return;

      if (splitInstance.current) {
        splitInstance.current.revert();
      }

      splitInstance.current = new SplitType(textElement as HTMLElement, {
        types: 'words,chars',
        tagName: 'span',
      }) as unknown as SplitTypeInstance;

      // Prepare characters as hidden before revealing the text container
      gsap.set(splitInstance.current.chars, { opacity: 0, y: 32 });
      gsap.set(textElement, { opacity: 1 });

      // Create and play animation
      animationRef.current = gsap.timeline({
        onComplete: () => {
          setIsLoading(false);
          cleanupAnimation();
          onComplete?.();
        },
      });

      animationRef.current
        // Brief pause on pure blank white screen
        .to({}, { duration: 0.2 })
        // Animate text characters appearing smoothly from below
        .to(splitInstance.current.chars, {
          opacity: 1,
          y: 0,
          duration: 0.25,
          stagger: 0.035,
          ease: 'power3.out',
        })
        // Pause after text finishes - stays readable
        .to({}, { duration: 0.5 })
        // Fade out preloader to seamlessly reveal page underneath
        .to(preloaderContainerRef.current, {
          opacity: 0,
          duration: 0.6,
          ease: 'power2.inOut',
        });
    }, 50);

    return () => {
      clearTimeout(timer);
      cleanupAnimation();
    };
  }, [currentRoute, cleanupAnimation, onComplete]);

  if (!isLoading) return null;

  return (
    <div
      ref={preloaderContainerRef}
      className="fixed inset-0 w-full h-screen bg-white text-secondary-500 z-[99999] flex items-center justify-center pointer-events-auto"
      aria-live="polite"
      aria-busy={isLoading}
    >
      <span
        className="text-preloader opacity-0 w-11/12 text-center text-[#E25C23] title uppercase font-extrabold lg:text-6xl md:text-5xl sm:text-4xl text-2xl tracking-wider select-none"
        aria-label="Loading, please wait"
      >
        EXPLORE, EXPERIENCE, EXPAND.
      </span>
    </div>
  );
};

export default Preloader;
